<!-- Login page component that handles user authentication with multiple login types 
Supports email, mobile, and UID-PIN authentication methods 
Displays a responsive layout with hero section and login form -->

<template>
  <!-- AppLoader component -->
  <AppLoader v-if="isLoading" :is-loading="isLoading" />

  <!-- Login page content -->
  <div v-else class="login-container">
    <v-row>
      <v-col cols="12" md="7" class="login-section">
        <div class="hero-content">
          <!-- Hero background image -->
          <img
            loading="lazy"
            :src="
              loginPageData[
                `${size}_bg_image` as 'desktop_bg_image' | 'mobile_bg_image'
              ] || HeroBackground
            "
            alt="Login background"
            class="hero-background"
          />
          <!-- Hero message section -->
          <div class="hero-message">
            <!-- Hero logo -->
            <img
              loading="lazy"
              :src="loginPageData.logo || hypLogo"
              alt="HYP Logo"
              class="hero-logo"
            />
            <!-- Hero text -->
            <p class="hero-text">
              {{
                loginPageData.desktop_offer_detail || LOGIN_CONSTANTS.HERO_TEXT
              }}
            </p>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="5" class="auth-section">
        <div class="auth-content">
          <div
            class="responsive-hero-message"
            :style="{
              // backgroundImage: `url(${loginPageData.mobile_bg_image})`,
              // backgroundSize: 'cover',
              // backgroundPosition: 'center',
              // backgroundRepeat: 'no-repeat',
            }"
          >
            <!-- Hero logo -->
            <img
              loading="lazy"
              :src="loginPageData.logo || hypLogo"
              alt="HYP Logo"
              class="hero-logo"
            />
            <h6 class="hero-text">
              {{
                loginPageData.mobile_offer_detail ||
                LOGIN_CONSTANTS.RESPONSIVE_HERO_MESSAGE
              }}
            </h6>
          </div>

          <!-- Login type specific components -->
          <!-- UID/PIN sign-in component -->
          <UidPinSignin v-if="loginType === LOGIN_CONSTANTS.UID_TYPE" />
          <!-- Email sign-in component -->
          <EmailSignin v-else-if="loginType === LOGIN_CONSTANTS.EMAIL_TYPE" />
          <!-- Mobile sign-in component -->
          <MobileSignin v-else-if="loginType === LOGIN_CONSTANTS.MOBILE_TYPE" />

          <!-- Terms and contact information -->
          <p v-if="loginType !== ''" class="terms-text">
            {{ LOGIN_CONSTANTS.TERMS_TEXT }}
            <span class="terms-link">
              {{ LOGIN_CONSTANTS.TERMS_LINK }}
            </span>
          </p>
          <!-- Contact information -->
          <div class="contact-info">
            <span v-if="loginPageData.email" class="contact-email">
              {{ loginPageData.email || LOGIN_CONSTANTS.CONTACT_EMAIL }}
            </span>
            <span
              v-if="loginPageData.email && loginPageData.mobile_number"
              class="divider"
            />
            <span v-if="loginPageData.mobile_number" class="contact-phone">
              {{
                `${loginPageData.phone_code} ${loginPageData.mobile_number}` ||
                LOGIN_CONSTANTS.CONTACT_PHONE
              }}
            </span>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
// Constants and asset imports
import { LOGIN_CONSTANTS } from "~/constants/login-constants";
import HeroBackground from "~/assets/images/login/hero-background.min.png";
import hypLogo from "~/assets/hyp-logo.min.png";
import type { ILoginTypeResponse } from "~/types/login";
import { API_ENDPOINT } from "~/constants/api-endpoint";
import { getOrigin } from "~/lib/config/host";

// Page metadata configuration
definePageMeta({
  layout: "login",
  title: "Login",
});

// Composables and utilities initialization
const { handleErrorResponse } = useErrors();
const { currentSize } = useScreenSize();
const router = useRouter();
const { isLoading } = useCommon();

// Computed property to determine screen size for responsive image loading
const size = computed<string>(() => {
  return ["sm", "xs"].includes(currentSize.value) ? "mobile" : "desktop";
});

// State management
const loginType = useState<string>("loginType", () => "");
const organizationId = useState<string>("organizationId", () => "");
const errorMessage = ref<string>("");

// Store initialization
const loginStore = useLoginStore();
const errorStore = useErrorStore();

// Interface for login page configuration and branding
const loginPageData = ref<ILoginTypeResponse["data"]>({
  organization_id: "",
  login_type: "",
  login_type_text: "",
  logo: "",
  desktop_offer_detail: "",
  mobile_offer_detail: "",
  desktop_bg_image: "",
  mobile_bg_image: "",
  email: "",
  mobile_number: "",
  phone_code: "",
  has_referral: "",
  is_public_access: false,
});

/**
 * Fetches login type configuration from the server
 * Sets up organization-specific login method and branding
 * Handles error cases and redirects to error page if needed
 */
const fetchLoginTypeData = async () => {
  isLoading.value = true;
  const origin = getOrigin();
  const body = {
    url: origin,
  };
  try {
    // Fetch login type data from the API
    const response = await useApi().post<ILoginTypeResponse>(
      API_ENDPOINT.LOGIN_TYPE,
      body,
    );
    // Set the login type and organization ID
    loginType.value = response?.data?.login_type;
    organizationId.value = response?.data?.organization_id;
    loginPageData.value = response?.data;

    // Store login type data in the store
    loginStore.setLoginTypeData({
      organization_id: response?.data?.organization_id,
      login_type: response?.data?.login_type,
      has_referral: response?.data?.has_referral,
      is_public_access: response?.data?.is_public_access,
    });

    // Update the document head with the logo
    useHead({
      link: [{ rel: "icon", type: "image/x-icon", href: response?.data?.logo }],
    });

    return response;
  } catch (error: any) {
    // Handle error response
    errorMessage.value = handleErrorResponse(error.response).error;

    // Set the login type error
    errorStore.setLoginTypeError(true);

    // Show the error message
    errorStore.setErrorMessage(errorMessage.value);

    // Redirect to error page
    router.push("error");
  } finally {
    // Reset loading state
    isLoading.value = false;
  }
};

// Initialize login configuration on component mount
onMounted(async () => {
  await fetchLoginTypeData();
});
</script>
